import { addDeviceHeaders, handleDeviceLimitError } from './deviceFingerprint';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Enhanced API client with device fingerprinting and error handling
 */
class ApiClient {
  constructor() {
    this.baseURL = API_URL;
  }

  /**
   * Make an authenticated API request with device headers
   */
  async request(endpoint, options = {}) {
    const token = localStorage.getItem('atpl_access_token');
    
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      ...options,
    };

    // Add device fingerprint headers
    addDeviceHeaders(config);

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, config);
      
      // Handle device limit errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const deviceError = handleDeviceLimitError({ response: { status: response.status, data: errorData } });
        
        if (deviceError.isDeviceLimitError) {
          // Show device limit modal or redirect to device management
          this.handleDeviceLimitExceeded(deviceError);
          throw new Error(deviceError.message);
        }
        
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Handle device limit exceeded error
   */
  handleDeviceLimitExceeded(deviceError) {
    // Show a modal or notification
    const message = `${deviceError.message}\n\nYou can manage your devices in the account settings.`;
    
    if (window.confirm(`${message}\n\nWould you like to go to device management now?`)) {
      // Redirect to device management page
      window.location.href = '/account/devices';
    } else {
      // Log out the user
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
  }

  /**
   * GET request
   */
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * Device management methods
   */
  async getDevices() {
    return this.get('/api/auth/devices/');
  }

  async getDeviceStatus() {
    return this.get('/api/auth/devices/status/');
  }

  async removeDevice(deviceId) {
    return this.delete(`/api/auth/devices/${deviceId}/`);
  }

  async logoutAllDevices() {
    return this.post('/api/auth/devices/logout-all/');
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;
